/**
 * ERPs Handlers
 */

import {
  ERPsState,
  createInitialFormData,
  createInitialDetailFormData,
  DEFAULT_ERP_DETAIL_TAB,
  DEFAULT_DRIFT_FILTER
} from './state';
import { ERPsCommand } from './commands';
import { CommandHandlerMap } from '@commandDrivenStateEngine/core/types';

type ExtractCommand<T extends string> = Extract<ERPsCommand, { type: T }>;

// Data handlers
const handleDataUpsert = (
  state: ERPsState,
  cmd: ExtractCommand<'integrations/erps/data/upsert'>
): ERPsState => ({
  ...state,
  erps: { ...state.erps, [cmd.payload.erp.id]: cmd.payload.erp }
});

const handleDataRemove = (
  state: ERPsState,
  cmd: ExtractCommand<'integrations/erps/data/remove'>
): ERPsState => {
  const { [cmd.payload.erpId]: _, ...rest } = state.erps;
  return { ...state, erps: rest };
};

const handleDataSetAll = (
  state: ERPsState,
  cmd: ExtractCommand<'integrations/erps/data/setAll'>
): ERPsState => ({
  ...state,
  erps: cmd.payload.erps
});

// Detail handlers
const handleDetailOpen = (
  state: ERPsState,
  cmd: ExtractCommand<'integrations/erps/detail/open'>
): ERPsState => {
  const erp = state.erps[cmd.payload.erpId];
  if ( !erp ) return state;

  // Re-opening the SAME integration keeps the selected tab. The view dispatches
  // `open` once the integration has loaded, which on a refresh happens after the
  // tab was already restored from `?tab=` — resetting here would undo it.
  const alreadyOpen =
    state.erpDetail.mode.type === 'view' && state.erpDetail.mode.erpId === cmd.payload.erpId;

  return {
    ...state,
    erpDetail: {
      mode: { type: 'view', erpId: cmd.payload.erpId },
      activeTab: alreadyOpen ? state.erpDetail.activeTab : DEFAULT_ERP_DETAIL_TAB,
      isEditing: false,
      formData: { label: erp.channelUserName || '' },
      formErrors: {}
    }
  };
};

const handleDetailClose = ( state: ERPsState ): ERPsState => ({
  ...state,
  erpDetail: {
    mode: { type: 'closed' },
    activeTab: DEFAULT_ERP_DETAIL_TAB,
    isEditing: false,
    formData: createInitialDetailFormData(),
    formErrors: {}
  }
});

const handleDetailSetTab = (
  state: ERPsState,
  cmd: ExtractCommand<'integrations/erps/detail/setTab'>
): ERPsState => ({
  ...state,
  erpDetail: { ...state.erpDetail, activeTab: cmd.payload.tab }
});

const handleDetailStartEdit = ( state: ERPsState ): ERPsState => ({
  ...state,
  erpDetail: { ...state.erpDetail, isEditing: true }
});

const handleDetailCancelEdit = ( state: ERPsState ): ERPsState => ({
  ...state,
  erpDetail: { ...state.erpDetail, isEditing: false, formErrors: {}}
});

const handleDetailUpdateField = (
  state: ERPsState,
  cmd: ExtractCommand<'integrations/erps/detail/updateField'>
): ERPsState => {
  // Remove the error for the updated field
  const { [cmd.payload.field]: _, ...remainingErrors } = state.erpDetail.formErrors;
  return {
    ...state,
    erpDetail: {
      ...state.erpDetail,
      formData: { ...state.erpDetail.formData, [cmd.payload.field]: cmd.payload.value },
      formErrors: remainingErrors
    }
  };
};

const handleDetailSave = ( state: ERPsState ): ERPsState => ({
  ...state,
  loading: { ...state.loading, submitting: true }
});

// Add modal handlers
const handleAddModalOpen = ( state: ERPsState ): ERPsState => ({
  ...state,
  addERPModal: {
    isOpen: true,
    step: 'selectType',
    selectedType: null,
    formData: createInitialFormData(),
    formErrors: {}
  }
});

const handleAddModalClose = ( state: ERPsState ): ERPsState => ({
  ...state,
  addERPModal: { ...state.addERPModal, isOpen: false }
});

const handleAddModalSelectType = (
  state: ERPsState,
  cmd: ExtractCommand<'integrations/erps/addModal/selectType'>
): ERPsState => ({
  ...state,
  addERPModal: { ...state.addERPModal, selectedType: cmd.payload.erpType, step: 'enterDetails' }
});

const handleAddModalUpdateField = (
  state: ERPsState,
  cmd: ExtractCommand<'integrations/erps/addModal/updateField'>
): ERPsState => ({
  ...state,
  addERPModal: {
    ...state.addERPModal,
    formData: { ...state.addERPModal.formData, [cmd.payload.field]: cmd.payload.value }
  }
});

const handleAddModalSetStep = (
  state: ERPsState,
  cmd: ExtractCommand<'integrations/erps/addModal/setStep'>
): ERPsState => ({
  ...state,
  addERPModal: { ...state.addERPModal, step: cmd.payload.step }
});

const handleAddModalSubmit = ( state: ERPsState ): ERPsState => ({
  ...state,
  loading: { ...state.loading, submitting: true },
  addERPModal: { ...state.addERPModal, step: 'connecting' }
});

const handleAddModalReset = ( state: ERPsState ): ERPsState => ({
  ...state,
  addERPModal: {
    isOpen: state.addERPModal.isOpen,
    step: 'selectType',
    selectedType: null,
    formData: createInitialFormData(),
    formErrors: {}
  }
});

// Delete handlers
const handleDeleteOpen = (
  state: ERPsState,
  cmd: ExtractCommand<'integrations/erps/delete/open'>
): ERPsState => ({
  ...state,
  deleteModal: { isOpen: true, erpId: cmd.payload.erpId }
});

const handleDeleteClose = ( state: ERPsState ): ERPsState => ({
  ...state,
  deleteModal: { isOpen: false, erpId: null }
});

const handleDeleteConfirm = ( state: ERPsState ): ERPsState => ({
  ...state,
  loading: { ...state.loading, deleting: true }
});

// OAuth handlers
const handleOAuthStart = (
  state: ERPsState,
  cmd: ExtractCommand<'integrations/erps/oauth/start'>
): ERPsState => ({
  ...state,
  oauthRedirect: { isProcessing: true, code: cmd.payload.code, state: cmd.payload.state }
});

const handleOAuthComplete = ( state: ERPsState ): ERPsState => ({
  ...state,
  oauthRedirect: { isProcessing: false, code: null, state: null }
});

const handleOAuthFail = ( state: ERPsState ): ERPsState => ({
  ...state,
  oauthRedirect: { isProcessing: false, code: null, state: null }
});

// Loading handlers
const handleSetErpsLoading = (
  state: ERPsState,
  cmd: ExtractCommand<'integrations/erps/loading/setErps'>
): ERPsState => ({
  ...state,
  loading: { ...state.loading, erpsLoading: cmd.payload.isLoading }
});

const handleSetSubmitting = (
  state: ERPsState,
  cmd: ExtractCommand<'integrations/erps/loading/setSubmitting'>
): ERPsState => ({
  ...state,
  loading: { ...state.loading, submitting: cmd.payload.isLoading }
});

const handleSetDeleting = (
  state: ERPsState,
  cmd: ExtractCommand<'integrations/erps/loading/setDeleting'>
): ERPsState => ({
  ...state,
  loading: { ...state.loading, deleting: cmd.payload.isLoading }
});

// Drift audit handlers (TV1-2146)
const handleDriftLoad = ( state: ERPsState ): ERPsState => ({
  ...state,
  driftAudit: { ...state.driftAudit, loading: true }
});

const handleDriftDataLoaded = (
  state: ERPsState,
  cmd: ExtractCommand<'integrations/erps/drift/dataLoaded'>
): ERPsState => ({
  ...state,
  driftAudit: {
    ...state.driftAudit,
    // Rows are REPLACED wholesale, never merged: the policy re-runs the whole
    // page search, so a merge would resurrect rows the server no longer returns.
    rows: cmd.payload.rows,
    total: cmd.payload.total,
    loading: false
  }
});

const handleDriftSetFilter = (
  state: ERPsState,
  cmd: ExtractCommand<'integrations/erps/drift/setFilter'>
): ERPsState => ({
  ...state,
  driftAudit: {
    ...state.driftAudit,
    filter: { ...state.driftAudit.filter, ...cmd.payload },
    // A narrower filter can leave the current page past the end of the result
    // set, which would render an empty table the operator reads as "no drift".
    page: 1
  }
});

const handleDriftSetPage = (
  state: ERPsState,
  cmd: ExtractCommand<'integrations/erps/drift/setPage'>
): ERPsState => ({
  ...state,
  driftAudit: { ...state.driftAudit, page: cmd.payload.page }
});

const handleDriftClear = ( state: ERPsState ): ERPsState => ({
  ...state,
  // FRESH filter object and FRESH arrays on purpose: aliasing the exported
  // DEFAULT_DRIFT_FILTER const ( or initialState.driftAudit's arrays ) would let
  // any later reducer mutate shared module state for every store instance.
  driftAudit: {
    rows: [],
    total: 0,
    filter: { ...DEFAULT_DRIFT_FILTER },
    page: 1,
    loading: false,
    rechecking: [],
    recheckOutcomes: {}
  }
});

const handleDriftRecheckRequest = (
  state: ERPsState,
  cmd: ExtractCommand<'integrations/erps/drift/recheck/request'>
): ERPsState => {
  const { syncKey } = cmd.payload;
  // Idempotent append — a double click must not queue two spinners that a single
  // `settled` can only half-release.
  if ( state.driftAudit.rechecking.includes( syncKey )) return state;
  // A new attempt drops the previous verdict for this row: showing "not found"
  // next to a spinner would be a stale claim about a call still in flight.
  const remainingOutcomes = Object.fromEntries(
    Object.entries( state.driftAudit.recheckOutcomes ).filter(([key]) => key !== syncKey )
  );
  return {
    ...state,
    driftAudit: {
      ...state.driftAudit,
      rechecking: [ ...state.driftAudit.rechecking, syncKey ],
      recheckOutcomes: remainingOutcomes
    }
  };
};

const handleDriftRecheckSettled = (
  state: ERPsState,
  cmd: ExtractCommand<'integrations/erps/drift/recheck/settled'>
): ERPsState => ({
  ...state,
  driftAudit: {
    ...state.driftAudit,
    // Filter, not splice: settling an unknown syncKey ( a re-check that outlived
    // a `drift/clear`, or a duplicate settle ) is a no-op, never a throw.
    rechecking: state.driftAudit.rechecking.filter(( key ) => key !== cmd.payload.syncKey ),
    // The verdict is recorded only when the policy reports one; a settle without
    // an outcome ( e.g. a defensive release ) leaves any previous verdict alone.
    recheckOutcomes: cmd.payload.outcome === undefined
      ? state.driftAudit.recheckOutcomes
      : { ...state.driftAudit.recheckOutcomes, [cmd.payload.syncKey]: cmd.payload.outcome }
  }
});

export const handlers: CommandHandlerMap<ERPsState, ERPsCommand> = {
  'integrations/erps/data/upsert': handleDataUpsert,
  'integrations/erps/data/remove': handleDataRemove,
  'integrations/erps/data/setAll': handleDataSetAll,
  'integrations/erps/detail/open': handleDetailOpen,
  'integrations/erps/detail/close': handleDetailClose,
  'integrations/erps/detail/setTab': handleDetailSetTab,
  'integrations/erps/detail/startEdit': handleDetailStartEdit,
  'integrations/erps/detail/cancelEdit': handleDetailCancelEdit,
  'integrations/erps/detail/updateField': handleDetailUpdateField,
  'integrations/erps/detail/save': handleDetailSave,
  'integrations/erps/addModal/open': handleAddModalOpen,
  'integrations/erps/addModal/close': handleAddModalClose,
  'integrations/erps/addModal/selectType': handleAddModalSelectType,
  'integrations/erps/addModal/updateField': handleAddModalUpdateField,
  'integrations/erps/addModal/setStep': handleAddModalSetStep,
  'integrations/erps/addModal/submit': handleAddModalSubmit,
  'integrations/erps/addModal/reset': handleAddModalReset,
  'integrations/erps/delete/open': handleDeleteOpen,
  'integrations/erps/delete/close': handleDeleteClose,
  'integrations/erps/delete/confirm': handleDeleteConfirm,
  'integrations/erps/oauth/start': handleOAuthStart,
  'integrations/erps/oauth/complete': handleOAuthComplete,
  'integrations/erps/oauth/fail': handleOAuthFail,
  'integrations/erps/loading/setErps': handleSetErpsLoading,
  'integrations/erps/loading/setSubmitting': handleSetSubmitting,
  'integrations/erps/loading/setDeleting': handleSetDeleting,
  'integrations/erps/drift/load': handleDriftLoad,
  'integrations/erps/drift/dataLoaded': handleDriftDataLoaded,
  'integrations/erps/drift/setFilter': handleDriftSetFilter,
  'integrations/erps/drift/setPage': handleDriftSetPage,
  'integrations/erps/drift/clear': handleDriftClear,
  'integrations/erps/drift/recheck/request': handleDriftRecheckRequest,
  'integrations/erps/drift/recheck/settled': handleDriftRecheckSettled
};
